import { useCallback, useMemo, useState } from 'react';
import { Option } from '@common-fe/common-fe';

import { IReturnedCheckDto, IReturnedCheckDtoCheckTypeEnum } from '@/dtos/models';

import useProcessReturnedChecksQuery from '../queries/useProcessReturnedChecks.query';
import useReturnedCheckPaymentMethodsQuery from '../queries/useReturnedCheckPaymentMethods.query';
import { CheckDecisions, CheckProcessingType } from '../returnedChecks.types';

export interface ReturnedChecksProps {
  providerChecks: IReturnedCheckDto[];
  participantChecks: IReturnedCheckDto[];
  checkDecisions: CheckDecisions;
  paymentMethodOptions: Option[];
  onlyCheckLeft: boolean;
  onSelectCheckAction: (lockboxId: number, type: CheckProcessingType) => void;
  onSelectCheckPaymentMethod: (lockboxId: number, paymentMethodId: number) => void;
}

interface Props {
  providerChecks: IReturnedCheckDto[];
  participantChecks: IReturnedCheckDto[];
  employeeId?: string;
  excludeCheckPaymentMethod: boolean;
}

const useReturnedChecks = ({
  providerChecks,
  participantChecks,
  employeeId,
  excludeCheckPaymentMethod,
}: Props) => {
  const [checkDecisions, setCheckDecisions] = useState<CheckDecisions>({});
  const { processReturnedChecks, isLoading: isProcessingChecks } = useProcessReturnedChecksQuery();

  const { paymentMethodOptions, onlyCheckLeft } = useReturnedCheckPaymentMethodsQuery({
    employeeId,
    excludeCheck: excludeCheckPaymentMethod,
  });

  const hasChecks = providerChecks.length > 0 || participantChecks.length > 0;

  const allChecks = useMemo(
    () => [...providerChecks, ...participantChecks],
    [providerChecks, participantChecks]
  );

  const handleSelectCheckAction = useCallback((lockboxId: number, type: CheckProcessingType) => {
    setCheckDecisions((prev) => ({
      ...prev,
      [lockboxId]: { type },
    }));
  }, []);

  const handleSelectCheckPaymentMethod = useCallback(
    (lockboxId: number, paymentMethodId: number) => {
      setCheckDecisions((prev) => ({
        ...prev,
        [lockboxId]: { type: CheckProcessingType.REISSUE, paymentMethodId },
      }));
    },
    []
  );

  const resetCheckDecisions = useCallback(() => {
    setCheckDecisions({});
  }, []);

  const processChecksIfNeeded = useCallback(async () => {
    if (hasChecks && Object.keys(checkDecisions).length > 0) {
      await processReturnedChecks(checkDecisions);
    }
  }, [hasChecks, checkDecisions, processReturnedChecks]);

  const areCheckDecisionsComplete = useMemo(() => {
    if (!hasChecks) {
      return true;
    }
    return allChecks.every((check) => {
      const decision = checkDecisions[check.lockbox_id as number];
      if (!decision) {
        return false;
      }
      if (
        decision.type === CheckProcessingType.REISSUE
        && check.check_type === IReturnedCheckDtoCheckTypeEnum.PARTICIPANT
        && !onlyCheckLeft
      ) {
        return decision.paymentMethodId !== undefined;
      }
      return true;
    });
  }, [hasChecks, allChecks, checkDecisions, onlyCheckLeft]);

  const checksProps = useMemo<ReturnedChecksProps>(
    () => ({
      providerChecks,
      participantChecks,
      checkDecisions,
      paymentMethodOptions,
      onlyCheckLeft,
      onSelectCheckAction: handleSelectCheckAction,
      onSelectCheckPaymentMethod: handleSelectCheckPaymentMethod,
    }),
    [
      providerChecks,
      participantChecks,
      checkDecisions,
      paymentMethodOptions,
      onlyCheckLeft,
      handleSelectCheckAction,
      handleSelectCheckPaymentMethod,
    ]
  );

  return {
    hasChecks,
    isProcessingChecks,
    areCheckDecisionsComplete,
    checksProps,
    processChecksIfNeeded,
    resetCheckDecisions,
  };
};

export default useReturnedChecks;
